import type { ImageInstanceRasterPaint, ImageInstanceRasterPaintKey, Layer } from '../types/layer.js';
import {
  IMAGE_INSTANCE_RASTER_PAINT_DEFAULTS,
  IMAGE_INSTANCE_RASTER_PAINT_KEYS,
} from '../types/layer.js';
import { getImageOverlayInstances } from './normalizeImageLayerSource.js';

/** Extract numeric raster-* keys from a flat MapLibre-style paint object. */
export function sliceRasterPaintFromLayerPaint(
  paint: Record<string, unknown> | undefined,
): Record<string, number> {
  if (!paint || typeof paint !== 'object') {
    return {};
  }
  const out: Record<string, number> = {};
  for (const [k, v] of Object.entries(paint)) {
    if (k.startsWith('raster-') && typeof v === 'number' && isFinite(v)) {
      out[k] = v;
    }
  }
  return out;
}

/** Keep only known image-instance raster keys with finite numbers. */
export function pickKnownInstanceRasterPaint(
  raw: ImageInstanceRasterPaint | undefined,
): ImageInstanceRasterPaint {
  if (!raw || typeof raw !== 'object') {
    return {};
  }
  const out: ImageInstanceRasterPaint = {};
  for (const key of IMAGE_INSTANCE_RASTER_PAINT_KEYS) {
    const v = raw[key];
    if (typeof v === 'number' && isFinite(v)) {
      out[key] = v;
    }
  }
  return out;
}

/**
 * Resolved MapLibre raster paint for one image overlay instance.
 * Precedence: instance rasterPaint overrides legacy layer-level raster-* paint; then defaults.
 */
export function resolveImageInstanceRasterPaint(
  layerPaint: Record<string, unknown> | undefined,
  instanceRasterPaint: ImageInstanceRasterPaint | undefined,
): Record<ImageInstanceRasterPaintKey, number> {
  const layerSlice = sliceRasterPaintFromLayerPaint(layerPaint);
  const instSlice = pickKnownInstanceRasterPaint(instanceRasterPaint);
  const merged: Record<ImageInstanceRasterPaintKey, number> = {
    ...IMAGE_INSTANCE_RASTER_PAINT_DEFAULTS,
  };
  for (const key of IMAGE_INSTANCE_RASTER_PAINT_KEYS) {
    if (layerSlice[key] !== undefined) {
      merged[key] = layerSlice[key]!;
    }
  }
  for (const key of IMAGE_INSTANCE_RASTER_PAINT_KEYS) {
    if (instSlice[key] !== undefined) {
      merged[key] = instSlice[key]!;
    }
  }
  return merged;
}

/** Remove all raster-* keys from a paint object (mutates copy only). */
export function stripRasterKeysFromPaint(
  paint: Record<string, unknown> | undefined,
): Record<string, unknown> {
  if (!paint || typeof paint !== 'object') {
    return {};
  }
  const next: Record<string, unknown> = { ...paint };
  for (const k of Object.keys(next)) {
    if (k.startsWith('raster-')) {
      delete next[k];
    }
  }
  return next;
}

/**
 * Move legacy layer-level raster paint onto each image instance and strip from layer.
 * Idempotent if layer paint has no raster-* keys. Uses getImageOverlayInstances (legacy single-image OK).
 */
export function migrateImageLayerRasterPaintToInstances(layer: Layer): Layer {
  if (layer.type !== 'image') {
    return layer;
  }
  const paint = layer.styleConfig?.paint as Record<string, unknown> | undefined;
  const layerRaster = sliceRasterPaintFromLayerPaint(paint);
  if (Object.keys(layerRaster).length === 0) {
    return layer;
  }
  const instances = getImageOverlayInstances(layer.sourceConfig, layer.id);
  if (instances.length === 0) {
    return layer;
  }
  const newInstances = instances.map((inst) => ({
    ...inst,
    rasterPaint: resolveImageInstanceRasterPaint(paint, inst.rasterPaint),
  }));
  const prevStyle = layer.styleConfig;
  const newPaint = stripRasterKeysFromPaint(paint);
  return {
    ...layer,
    sourceConfig: {
      ...layer.sourceConfig,
      imageInstances: newInstances,
    },
    styleConfig: prevStyle
      ? {
          ...prevStyle,
          paint: newPaint,
        }
      : prevStyle,
  };
}
